import { useCallback, useEffect, useRef, useState } from "react";
import { registerSW } from "virtual:pwa-register";

/**
 * Une PWA installée peut rester ouverte des jours : sans sondage, elle
 * n'apprendrait l'existence d'une version qu'au prochain démarrage à froid.
 */
const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1_000;

/** Laisse le temps de lire l'avis avant que la page ne se recharge. */
const AUTO_APPLY_DELAY_MS = 1_500;

export interface AppUpdate {
  /** Une version est téléchargée et n'attend plus que l'activation. */
  ready: boolean;
  /** Applique la mise à jour : active le nouveau worker puis recharge. */
  apply: () => void;
}

/**
 * Le rechargement est décidé ici, jamais par le service worker lui-même : une
 * page qui se recharge au milieu d'un tour ferait perdre la main au joueur.
 * Tant que `safeToReload` est faux, la version reste en attente.
 */
export const useAppUpdate = ({ safeToReload }: { safeToReload: boolean }): AppUpdate => {
  const [ready, setReady] = useState(false);
  const updateRef = useRef<((reload?: boolean) => Promise<void>) | null>(null);
  const appliedRef = useRef(false);

  useEffect(() => {
    // StrictMode monte deux fois : une seule inscription doit survivre.
    if (updateRef.current !== null) return;

    let registration: ServiceWorkerRegistration | undefined;
    let timer: number | null = null;
    // L'inscription est différée : son rappel peut donc revenir après le
    // démontage, et poserait un intervalle que plus personne n'arrêterait.
    let cancelled = false;

    const check = () => {
      // Hors ligne, `update()` rejette : l'échec ne doit rien interrompre.
      void registration?.update().catch(() => {});
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };

    // Sans `immediate`, l'inscription attend l'événement `load` : le
    // pré-chargement du worker — plusieurs centaines de kilo-octets à
    // révision, donc toujours absents du cache — ne dispute plus la bande
    // passante et le processeur à la première image de l'application.
    // `injectRegister: null` et `skipWaiting: false` restent intacts : le
    // moment du rechargement continue d'être décidé ici.
    updateRef.current = registerSW({
      onNeedRefresh() {
        setReady(true);
      },
      onRegisteredSW(_url, swRegistration) {
        registration = swRegistration;
        if (cancelled || swRegistration === undefined) return;
        timer = window.setInterval(check, UPDATE_CHECK_INTERVAL_MS);
        document.addEventListener("visibilitychange", onVisible);
      },
      onRegisterError() {
        // Un worker indisponible ne doit pas empêcher de jouer.
      },
    });

    return () => {
      cancelled = true;
      if (timer !== null) window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  const apply = useCallback(() => {
    if (appliedRef.current) return;
    appliedRef.current = true;
    void updateRef.current?.(true);
  }, []);

  useEffect(() => {
    if (!ready || !safeToReload) return;
    const timer = window.setTimeout(apply, AUTO_APPLY_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [apply, ready, safeToReload]);

  return { ready, apply };
};
